import {format, subDays, endOfMonth} from "date-fns";
import {toZonedTime} from "date-fns-tz";
import {handleDatabaseError} from "../utils";
import type {
  DbClient,
  CreateActivityInput,
  CancelActivityInput,
  UpdateActivityDateInput,
  ListActivitiesInput,
  ActivityStatsInput,
  HostessActivityStatsInput,
  ActivityCounts,
  BatchActivityStatsInput,
  HostessActivityInsightsInput,
  HostessActivityInsights,
} from "./activity-types";

const TZ_JST = "Asia/Tokyo";

/** サマリーの日毎集計の窓（直近 N 日） */
const DAILY_WINDOW_DAYS = 30;

/**
 * 営業活動を作成
 */
export async function createActivity(client: DbClient, input: CreateActivityInput) {
  const {data, error} = await client
    .from("activities")
    .insert({
      store_id: input.store_id,
      customer_id: input.customer_id,
      hostess_id: input.hostess_id,
      activity_type: input.activity_type,
      activity_date: input.activity_date || new Date().toISOString(),
      notes: input.notes || null,
    })
    .select()
    .single();

  if (error) {
    handleDatabaseError(error);
  }

  return data;
}

/**
 * 営業活動をキャンセル
 */
export async function cancelActivity(client: DbClient, input: CancelActivityInput) {
  // 存在確認
  const {data: existing, error: findError} = await client
    .from("activities")
    .select("id")
    .eq("id", input.id)
    .is("cancelled_at", null)
    .single();

  if (findError || !existing) {
    throw new Error("営業活動が見つかりません");
  }

  const updateData: Record<string, unknown> = {
    cancelled_at: new Date().toISOString(),
    cancel_reason: input.cancel_reason || null,
  };

  if (input.cancelled_by) {
    updateData.cancelled_by = input.cancelled_by;
  }

  const {error} = await client.from("activities").update(updateData).eq("id", input.id);

  if (error) {
    handleDatabaseError(error);
  }
}

/**
 * 営業活動の activity_date を訂正する
 * 変更履歴は DB トリガーで activity_date_history に自動記録される
 */
export async function updateActivityDate(client: DbClient, input: UpdateActivityDateInput) {
  const {data: existing, error: findError} = await client
    .from("activities")
    .select("id")
    .eq("id", input.id)
    .is("cancelled_at", null)
    .single();

  if (findError || !existing) {
    throw new Error("営業活動が見つかりません");
  }

  const {error} = await client
    .from("activities")
    .update({activity_date: input.activity_date})
    .eq("id", input.id);

  if (error) {
    handleDatabaseError(error);
  }
}

/**
 * 営業活動一覧を取得
 *
 * include_relations=true の時、customer.company_name は
 * docs/domain/20260527-customer-company-context-display.md の
 * 「ホステス視点」ロジックで動的解決する。
 * 各 activity に紐づく hostess_id × customer_id でその activity を行ったホステスが
 * 持っている代表名刺の company を返す (is_primary > scanned_at > id 最先頭)。
 */
export async function listActivities(client: DbClient, input: ListActivitiesInput) {
  const limit = input.limit ?? 100;
  const offset = input.offset ?? 0;

  const selectColumns = input.include_relations
    ? `
        *,
        customer:customers(id, name),
        store:stores(id, store_name, logo_path),
        hostess:hostesses!activities_hostess_id_fkey(id, name)
      `
    : "*";

  let query = client
    .from("activities")
    .select(selectColumns, {count: "exact"})
    .is("cancelled_at", null)
    .order("activity_date", {ascending: false})
    .order("created_at", {ascending: false})
    .range(offset, offset + limit - 1);

  if (input.customer_id) {
    query = query.eq("customer_id", input.customer_id);
  }
  if (input.hostess_id) {
    query = query.eq("hostess_id", input.hostess_id);
  }
  if (input.store_id) {
    query = query.eq("store_id", input.store_id);
  }
  if (input.store_ids && input.store_ids.length > 0) {
    query = query.in("store_id", input.store_ids);
  }
  if (input.activity_type) {
    query = query.eq("activity_type", input.activity_type);
  }
  if (input.date_from) {
    query = query.gte("activity_date", input.date_from);
  }
  if (input.date_to) {
    query = query.lte("activity_date", input.date_to);
  }

  const {data, error, count} = await query;

  if (error) {
    handleDatabaseError(error);
  }

  // include_relations の select 文字列は型パーサが ParserError union を返すため二段キャスト
  const rows = (data ?? []) as unknown as Array<Record<string, unknown>>;

  if (!input.include_relations || rows.length === 0) {
    return {data: rows, count: count || 0};
  }

  // ホステス視点での会社名解決:
  // (hostess_id, customer_id) ごとに、自分の名刺集合内で
  // is_primary > scanned_at > id 最先頭の company.name を取得する。
  const pairs = new Set<string>();
  for (const row of rows) {
    const hostessId = row.hostess_id as string | null | undefined;
    const customerId = row.customer_id as string | null | undefined;
    if (hostessId && customerId) {
      pairs.add(`${hostessId}::${customerId}`);
    }
  }

  const companyByPair = new Map<string, string>();
  if (pairs.size > 0) {
    const hostessIds = Array.from(new Set(Array.from(pairs).map((p) => p.split("::")[0])));
    const customerIds = Array.from(new Set(Array.from(pairs).map((p) => p.split("::")[1])));

    const {data: cardRows, error: cardError} = await client
      .from("business_cards")
      .select("hostess_id, customer_id, is_primary, scanned_at, id, companies!inner(name, deleted_at)")
      .in("hostess_id", hostessIds)
      .in("customer_id", customerIds)
      .not("company_id", "is", null)
      .order("is_primary", {ascending: false})
      .order("scanned_at", {ascending: false, nullsFirst: false})
      .order("id", {ascending: false});

    if (cardError) {
      handleDatabaseError(cardError);
    }

    type CardRow = {
      hostess_id: string | null;
      customer_id: string | null;
      companies: {name: string | null; deleted_at: string | null} | null;
    };

    for (const card of (cardRows ?? []) as CardRow[]) {
      if (!card.hostess_id || !card.customer_id) continue;
      if (!card.companies || card.companies.deleted_at) continue;
      const key = `${card.hostess_id}::${card.customer_id}`;
      if (!pairs.has(key)) continue;
      if (!companyByPair.has(key) && card.companies.name) {
        companyByPair.set(key, card.companies.name);
      }
    }
  }

  const enrichedRows = rows.map((row) => {
    const hostessId = row.hostess_id as string | null | undefined;
    const customerId = row.customer_id as string | null | undefined;
    const customer = row.customer as {id: string; name: string | null} | null;
    if (!customer) return row;
    const key = hostessId && customerId ? `${hostessId}::${customerId}` : null;
    const company_name = key ? companyByPair.get(key) ?? null : null;
    return {
      ...row,
      customer: {
        ...customer,
        company_name,
      },
    };
  });

  return {data: enrichedRows, count: count || 0};
}

function emptyCounts(): ActivityCounts {
  return {
    phone_count: 0,
    message_count: 0,
    visit_count: 0,
    letter_count: 0,
    total_count: 0,
  };
}

function aggregateByType(rows: ReadonlyArray<{activity_type: string | null}>): ActivityCounts {
  const counts = emptyCounts();
  for (const a of rows) {
    switch (a.activity_type) {
      case "phone":
        counts.phone_count++;
        break;
      case "message":
        counts.message_count++;
        break;
      case "visit":
        counts.visit_count++;
        break;
      case "letter":
        counts.letter_count++;
        break;
    }
  }
  counts.total_count = counts.phone_count + counts.message_count + counts.visit_count + counts.letter_count;
  return counts;
}

const COUNTABLE_ACTIVITY_TYPES = ["phone", "message", "visit", "letter"] as const;

/**
 * 営業活動のタイプ別カウントを取得（ホステス全体・全顧客）
 *
 * days 未指定 = 全期間集計（ホステス詳細サマリーの既定挙動）。
 * days 指定時は activity_date >= JST(now - days) でフィルタ。
 *
 * activity_type ごとに head + count: 'exact' で 4 並列発行する。
 * PostgREST の暗黙 1000 行制限を回避し、転送量を 0 行に抑える。
 */
export async function getActivityCountsByHostess(
  client: DbClient,
  input: HostessActivityStatsInput,
): Promise<ActivityCounts> {
  let fromDateStr: string | undefined;
  if (input.days !== undefined) {
    const now = toZonedTime(new Date(), TZ_JST);
    fromDateStr = format(subDays(now, input.days), "yyyy-MM-dd");
  }

  const results = await Promise.all(
    COUNTABLE_ACTIVITY_TYPES.map(async (type) => {
      let q = client
        .from("activities")
        .select("*", {count: "exact", head: true})
        .eq("hostess_id", input.hostess_id)
        .is("cancelled_at", null)
        .eq("activity_type", type);
      if (fromDateStr) {
        q = q.gte("activity_date", fromDateStr);
      }
      const {count, error} = await q;
      if (error) {
        handleDatabaseError(error);
      }
      return [type, count ?? 0] as const;
    }),
  );

  const counts = emptyCounts();
  for (const [type, n] of results) {
    if (type === "phone") counts.phone_count = n;
    else if (type === "message") counts.message_count = n;
    else if (type === "visit") counts.visit_count = n;
    else if (type === "letter") counts.letter_count = n;
  }
  counts.total_count = counts.phone_count + counts.message_count + counts.visit_count + counts.letter_count;
  return counts;
}

/**
 * 営業活動のタイプ別カウントを取得（単一顧客）
 */
export async function getActivityCounts(client: DbClient, input: ActivityStatsInput): Promise<ActivityCounts> {
  const days = input.days ?? 30;
  const now = toZonedTime(new Date(), TZ_JST);
  const fromDateStr = format(subDays(now, days), "yyyy-MM-dd");

  let query = client
    .from("activities")
    .select("activity_type")
    .eq("customer_id", input.customer_id)
    .is("cancelled_at", null)
    .gte("activity_date", fromDateStr);

  if (input.hostess_id) {
    query = query.eq("hostess_id", input.hostess_id);
  }
  if (input.store_id) {
    query = query.eq("store_id", input.store_id);
  }

  const {data, error} = await query;

  if (error) {
    handleDatabaseError(error);
  }

  return aggregateByType(data || []);
}

/**
 * 複数顧客の営業活動カウントを一括取得
 */
export async function getBatchActivityCounts(
  client: DbClient,
  input: BatchActivityStatsInput,
): Promise<Map<string, ActivityCounts>> {
  const days = input.days ?? 30;
  const now = toZonedTime(new Date(), TZ_JST);
  const fromDateStr = format(subDays(now, days), "yyyy-MM-dd");

  const result = new Map<string, ActivityCounts>();

  if (input.customer_ids.length === 0) {
    return result;
  }

  // 初期化
  for (const id of input.customer_ids) {
    result.set(id, {
      phone_count: 0,
      message_count: 0,
      visit_count: 0,
      letter_count: 0,
      total_count: 0,
    });
  }

  let query = client
    .from("activities")
    .select("customer_id, activity_type")
    .in("customer_id", input.customer_ids)
    .is("cancelled_at", null)
    .gte("activity_date", fromDateStr);

  if (input.store_id) {
    query = query.eq("store_id", input.store_id);
  }

  const {data, error} = await query;

  if (error) {
    handleDatabaseError(error);
  }

  if (data) {
    for (const a of data) {
      const counts = result.get(a.customer_id);
      if (!counts) continue;

      switch (a.activity_type) {
        case "phone":
          counts.phone_count++;
          break;
        case "message":
          counts.message_count++;
          break;
        case "visit":
          counts.visit_count++;
          break;
        case "letter":
          counts.letter_count++;
          break;
      }
    }
  }

  // total_count を計算
  for (const counts of result.values()) {
    counts.total_count = counts.phone_count + counts.message_count + counts.visit_count + counts.letter_count;
  }

  return result;
}

/**
 * ホステス営業インサイト（admin 詳細「営業タブ」用）を取得する。
 *
 * 返すもの:
 *   - outreach: 対象月（input.month。未指定なら JST 当月）に何人へ営業したか（distinct）＋総数＋顧客別内訳
 *   - daily:    直近 30 日（JST 暦日）の日毎営業数（ゼロ埋め済み。month 指定に依らず固定窓）
 *
 * 集計は get_hostess_activity_insights RPC（SECURITY DEFINER + is_admin）で Postgres 側に寄せる。
 * activity_date は timestamptz のため、日境界・バケットはすべて JST で評価される（RPC 側）。
 * ここでは JST の「対象月」「直近30日」の暦日境界を算出して date を渡すだけ。
 */
export async function getHostessActivityInsights(
  client: DbClient,
  input: HostessActivityInsightsInput,
): Promise<HostessActivityInsights> {
  const nowJst = toZonedTime(new Date(), TZ_JST);
  const currentMonth = format(nowJst, "yyyy-MM");
  const todayStr = format(nowJst, "yyyy-MM-dd");
  const yesterdayStr = format(subDays(nowJst, 1), "yyyy-MM-dd");

  // 対象月: 'yyyy-MM' 指定があればその月、なければ JST 当月。
  // 月初・月末は暦フィールドのみ使うローカル Date で算出（TZ 無依存）。
  const targetMonth = input.month ?? currentMonth;
  const [y, m] = targetMonth.split("-").map(Number);
  const monthStart = new Date(y, m - 1, 1);
  const monthFrom = format(monthStart, "yyyy-MM-dd");

  // 締め日:
  //  - 過去月（対象月 ≠ 当月）は月末まで（全て確定）。
  //  - 当月は include_today で切替: false/未指定=前日まで（既定）、true=本日まで。
  //  - 月初(1日)で前日が前月に落ちる場合は本日(=月初)へフォールバック（空集計防止・二重ガード）。
  //    'yyyy-MM-dd' 文字列は辞書順=暦順なので比較で判定できる。
  let monthTo: string;
  if (targetMonth === currentMonth) {
    const cap = input.include_today ? todayStr : yesterdayStr;
    monthTo = cap < monthFrom ? monthFrom : cap;
  } else {
    monthTo = format(endOfMonth(monthStart), "yyyy-MM-dd");
  }

  const dailyTo = todayStr;
  // 当日を含めて DAILY_WINDOW_DAYS 日分にするため -(N-1) 日
  const dailyFrom = format(subDays(nowJst, DAILY_WINDOW_DAYS - 1), "yyyy-MM-dd");

  const {data, error} = await client.rpc("get_hostess_activity_insights", {
    p_hostess_id: input.hostess_id,
    p_month_from: monthFrom,
    p_month_to: monthTo,
    p_daily_from: dailyFrom,
    p_daily_to: dailyTo,
    p_customer_limit: input.customer_limit ?? 100,
  });

  if (error) {
    handleDatabaseError(error);
  }

  // RPC は jsonb（生成型では Json）を返すため、確定した形へ二段キャストする。
  const result = data as unknown as HostessActivityInsights | null;
  if (!result) {
    return {
      outreach: {
        month_from: monthFrom,
        month_to: monthTo,
        distinct_customer_count: 0,
        total_activity_count: 0,
        customers: [],
      },
      daily: [],
    };
  }
  return result;
}
